/**
 * Reservation load signal (schema §27) — display logic.
 *
 * Venues that take reservations show an ORIENTATIVE booked-ness status set by
 * staff ("fully booked tonight"), guidance rather than a guarantee — Resto is
 * still not a booking system. The status is only trusted for the Bucharest
 * calendar day it was asserted on: yesterday's "full" says nothing about
 * today, so a stale stamp degrades to the neutral "takes reservations"
 * marker client-side (no cron/sweeper anywhere).
 */

import type { ReservationsStatus, Restaurant } from "@/types/restaurant";

// en-CA renders "YYYY-MM-DD" — a stable, comparable day string. Same Intl
// idiom as clock.ts; handles DST for Europe/Bucharest with no dependency.
const BUCHAREST_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Bucharest",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** What the reservation marker should show for this viewer, if anything. */
export type EffectiveReservationsStatus = ReservationsStatus | "neutral";

/**
 * True while the status stamp is from today (Bucharest day) — the only window
 * a staff-asserted status is trusted for. Also used by the manager editor to
 * render a stale status as "nothing selected" so re-asserting is a real click.
 */
export function isReservationsStatusFresh(
  stampedAt: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!stampedAt) return false;
  const stamped = new Date(stampedAt);
  if (Number.isNaN(stamped.getTime())) return false;
  return BUCHAREST_DAY.format(stamped) === BUCHAREST_DAY.format(now);
}

/**
 * Resolve the reservation signal for display.
 *
 * - `null` — venue doesn't take reservations: render nothing.
 * - `"neutral"` — passive "takes reservations" marker: guests (including auth
 *   `initializing`, so the surface fails toward the guest view), venues with
 *   no asserted status, and statuses stamped before today (Bucharest day).
 * - a `ReservationsStatus` — the staff-asserted level, signed-in only.
 */
export function effectiveReservationsStatus(
  restaurant: Pick<
    Restaurant,
    "reservations_enabled" | "reservations_status" | "reservations_status_updated_at"
  >,
  signedIn: boolean,
  now: Date = new Date(),
): EffectiveReservationsStatus | null {
  if (restaurant.reservations_enabled !== true) return null;
  if (!signedIn) return "neutral";
  const status = restaurant.reservations_status;
  if (!status) return "neutral";
  return isReservationsStatusFresh(restaurant.reservations_status_updated_at, now)
    ? status
    : "neutral";
}
